import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PrivacyAgreement from './components/PrivacyAgreement';
import AdminLayout from './components/AdminLayout';
import ProfilePage from './components/ProfilePage';
import AdminDashboard from './components/admin/Dashboard';
import AdminDepartmentWorkspacePicker from './components/admin/AdminDepartmentWorkspacePicker';
import UserManagement from './components/admin/UserManagement';
import RoleManagement from './components/admin/RoleManagement';
import DepartmentManagement from './components/admin/DepartmentManagement';
import AuditLogs from './components/admin/AuditLogs';
import SecurityCenter from './components/admin/SecurityCenter';
import SystemGovernance from './components/admin/SystemGovernance';
import DataPrivacyCompliance from './components/admin/DataPrivacyCompliance';
import ArchiveManagement from './components/admin/ArchiveManagement';
import ProtectedRoute from './components/ProtectedRoute';
import RoleDashboard from './components/operational/RoleDashboard';
import WorkspacePlaceholder from './components/operational/WorkspacePlaceholder';
import {
  InspectionReports,
  InspectorChainOfCustody,
  InspectorDashboard,
  InventoryReference,
  InspectorNotifications,
  ValidationResults,
} from './components/operational/inspector/InspectorWorkspace';
import {
  ComplianceNotifications,
  CustodyHistory,
  FirearmRegistry,
  FirearmReturns,
  IncidentManagement,
  IssuanceAndARE,
  LogisticsDashboard,
  LogisticsReports,
} from './components/operational/logistics/LogisticsWorkspace';
import {
  AccountableDashboard,
  AREUpload,
  AccountableInventoryLookup,
  AccountableIncidentView,
  AccountableIssuance,
  AccountableNotifications,
  AccountableReturns,
  OfficerRegistry,
} from './components/operational/accountable/AccountableWorkspace';
import { authService } from './services/authService';
import { notificationService } from './services/notificationService';
import { permissionService } from './services/permissionService';
import { privacyService } from './services/privacyService';
import { User } from './types/auth';
import { ErrorHandler } from './utils/errorHandler';
import {
  clearApplicationDateContext,
  configureApplicationDateContext,
  millisecondsUntilApplicationDateRollover,
} from './utils/dateValidation';
import { isUnreadNotificationStatus, NOTIFICATION_READ_EVENT, NotificationReadEventDetail } from './utils/notificationEvents';
import {
  DASHBOARD_ROUTES,
  getAllowedRoutes,
  getDashboardPath,
  getNavigationForWorkspace,
  getNavigationSectionsForWorkspace,
  WorkspaceKind,
} from './utils/roleRouting';
import {
  getAdminDepartmentIdFromSearch,
  isAdminUser,
  withAdminDepartment,
  withAdminDepartmentParam,
} from './utils/adminViewMode';

type OperationalRole = 'CENTRAL_LOGISTICS_OFFICER' | 'INSPECTION_OFFICER' | 'ACCOUNTABLE_PNP_OFFICER';

interface OperationalWorkspaceProps {
  user: User;
  workspace: Exclude<WorkspaceKind, 'admin'>;
  role: OperationalRole;
  title: string;
  sectionLabel: string;
  onLogout: () => Promise<void>;
}

const OperationalWorkspace: React.FC<OperationalWorkspaceProps> = ({
  user,
  workspace,
  role,
  title,
  sectionLabel,
  onLogout,
}) => {
  const location = useLocation();
  const dashboardPath = DASHBOARD_ROUTES[workspace];
  const adminViewMode = isAdminUser(user);
  const selectedDepartmentId = getAdminDepartmentIdFromSearch(location.search);
  const isDepartmentWorkspace = workspace === 'logistics' || workspace === 'accountable';
  const baseNavigationSections = getNavigationSectionsForWorkspace(user, workspace);
  const navigationSections = baseNavigationSections.map((section) => ({
    ...section,
    items: section.items.map((item) => {
      if (!adminViewMode || !selectedDepartmentId || !isDepartmentWorkspace) return item;
      return { ...item, path: withAdminDepartment(item.path, selectedDepartmentId) };
    }),
  }));
  const navigationItems = getNavigationForWorkspace(user, workspace)
    .filter((item) => item.path.startsWith(dashboardPath));
  const [notificationCount, setNotificationCount] = React.useState(0);
  const readNotificationIds = React.useRef(new Set<string>());
  const missingAdminDepartment = adminViewMode && isDepartmentWorkspace && !selectedDepartmentId;
  const isDepartmentScoped = !adminViewMode && (
    user.is_department_scoped || role === 'CENTRAL_LOGISTICS_OFFICER' || role === 'ACCOUNTABLE_PNP_OFFICER'
  );
  const primaryDepartment = user.primary_department;
  const selectedDepartment = user.departments?.find((department) => department.id === selectedDepartmentId);
  const scopeLabel = adminViewMode
    ? selectedDepartmentId
      ? `Administrator View: ${selectedDepartment?.name || `Department #${selectedDepartmentId}`}`
      : 'Administrator View: All Departments'
    : isDepartmentScoped
      ? `Department: ${primaryDepartment?.name || 'Not assigned'}`
      : 'Scope: All Departments';
  const legacyRoutePrefixes: Record<Exclude<WorkspaceKind, 'admin'>, string[]> = {
    inspector: ['inventory', 'evidence', 'missions', 'form-18', 'reconciliation', 'reports'],
    logistics: ['inventory', 'issuance-review', 'are-countersigning', 'compliance'],
    accountable: ['inventory', 'are-sign', 'custody'],
  };
  const stripQuery = (path: string): string => path.split('?')[0];
  const routeAllowed = navigationItems.some((item) => {
    const itemPath = stripQuery(item.path);
    return location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);
  }) || legacyRoutePrefixes[workspace].some((path) => location.pathname === `${dashboardPath}/${path}`);

  React.useEffect(() => {
    let mounted = true;
    if (!permissionService.hasPermission('notification.system.view')) {
      setNotificationCount(0);
      return () => { mounted = false; };
    }
    notificationService.getSystemNotifications(withAdminDepartmentParam({}, adminViewMode ? selectedDepartmentId : null))
      .then((items) => {
        if (mounted) setNotificationCount(items.filter((item) => isUnreadNotificationStatus(item.status)).length);
      })
      .catch(() => {
        if (mounted) setNotificationCount(0);
      });
    return () => { mounted = false; };
  }, [adminViewMode, location.pathname, selectedDepartmentId, user.permissions]);

  React.useEffect(() => {
    const handleNotificationRead = (event: Event) => {
      const detail = (event as CustomEvent<NotificationReadEventDetail>).detail;
      if (!detail || detail.nextStatus !== 'read' || !isUnreadNotificationStatus(detail.previousStatus)) return;
      const key = String(detail.id);
      if (readNotificationIds.current.has(key)) return;
      readNotificationIds.current.add(key);
      setNotificationCount((current) => Math.max(0, current - 1));
    };
    window.addEventListener(NOTIFICATION_READ_EVENT, handleNotificationRead);
    return () => window.removeEventListener(NOTIFICATION_READ_EVENT, handleNotificationRead);
  }, []);

  if (missingAdminDepartment) return <Navigate to={`/admin-dashboard/enter/${workspace}`} replace />;
  if (!routeAllowed) return <Navigate to={withAdminDepartment(dashboardPath, selectedDepartmentId)} replace />;

  const missingDepartment = isDepartmentScoped && !primaryDepartment;

  const inspectorRoutes = workspace === 'inspector' ? (
    <>
      <Route index element={<InspectorDashboard />} />
      <Route path="inspection-reports" element={<InspectionReports />} />
      <Route path="evidence" element={<Navigate to="../inspection-reports" replace />} />
      <Route path="validation-results" element={<ValidationResults />} />
      <Route path="notifications" element={<InspectorNotifications />} />
      <Route path="firearm-inventory-lookup" element={<InventoryReference />} />
      <Route path="chain-of-custody" element={<InspectorChainOfCustody />} />
      <Route path="inventory" element={<Navigate to="../firearm-inventory-lookup" replace />} />
      <Route path="custody" element={<Navigate to="../chain-of-custody" replace />} />
      <Route path="missions" element={<Navigate to="../inspection-reports" replace />} />
      <Route path="form-18" element={<Navigate to="../inspection-reports" replace />} />
      <Route path="reconciliation" element={<Navigate to="../validation-results" replace />} />
      <Route path="reports" element={<Navigate to="../inspection-reports" replace />} />
    </>
  ) : null;

  const logisticsRoutes = workspace === 'logistics' ? (
    <>
      <Route index element={<LogisticsDashboard />} />
      <Route path="firearm-registry" element={<FirearmRegistry />} />
      <Route path="issuance-are" element={<IssuanceAndARE />} />
      <Route path="returns" element={<FirearmReturns />} />
      <Route path="incidents" element={<IncidentManagement />} />
      <Route path="custody" element={<CustodyHistory />} />
      <Route path="reports" element={<LogisticsReports />} />
      <Route path="notifications" element={<ComplianceNotifications />} />
      <Route path="inventory" element={<Navigate to="../firearm-registry" replace />} />
      <Route path="issuance-review" element={<Navigate to="../issuance-are" replace />} />
      {/* Legacy standalone ARE countersigning route now redirects to the implemented Issuance & ARE workspace. */}
      <Route path="are-countersigning" element={<Navigate to="../issuance-are" replace />} />
      <Route path="compliance" element={<Navigate to="../notifications" replace />} />
    </>
  ) : null;

  const accountableRoutes = workspace === 'accountable' ? (
    <>
      <Route index element={<AccountableDashboard />} />
      <Route path="assigned-firearms" element={<AccountableInventoryLookup />} />
      <Route path="my-firearms" element={<Navigate to="../assigned-firearms" replace />} />
      <Route path="officers" element={<OfficerRegistry />} />
      <Route path="returns" element={<AccountableReturns />} />
      <Route path="incidents" element={<AccountableIncidentView />} />
      <Route path="notifications" element={<AccountableNotifications />} />
      <Route path="inventory" element={<Navigate to="../assigned-firearms" replace />} />
      <Route path="issuance" element={<AccountableIssuance />} />
      <Route path="are-upload" element={<AREUpload />} />
      {/* Legacy standalone ARE signing route now redirects to the implemented ARE upload workspace. */}
      <Route path="are-sign" element={<Navigate to="../are-upload" replace />} />
      <Route path="custody" element={<Navigate to="../assigned-firearms" replace />} />
    </>
  ) : null;

  return (
    <AdminLayout
      title={title}
      dashboardPath={dashboardPath}
      sectionLabel={sectionLabel}
      roleLabel={adminViewMode ? `Administrator View: ${sectionLabel}` : user.role_display}
      userName={user.username}
      onLogout={onLogout}
      navigationSections={navigationSections}
      breadcrumbs={[
        { label: sectionLabel, path: withAdminDepartment(dashboardPath, selectedDepartmentId) },
        {
          label: navigationItems.find((item) => {
            const itemPath = stripQuery(item.path);
            return location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);
          })?.label || title,
        },
      ]}
      notificationCount={notificationCount}
      notificationPath={adminViewMode && selectedDepartmentId
        ? withAdminDepartment(`${dashboardPath}/notifications`, selectedDepartmentId)
        : undefined}
      scopeLabel={scopeLabel}
      adminViewMode={adminViewMode}
      returnToAdminPath="/admin-dashboard"
      changeDepartmentPath={adminViewMode && isDepartmentWorkspace ? `/admin-dashboard/enter/${workspace}` : undefined}
    >
      {missingDepartment ? (
        <div className="workspace-placeholder">
          <h1>Department Assignment Required</h1>
          <p>Your account is not assigned to a department yet. Please contact an administrator before using this workspace.</p>
        </div>
      ) : (
        <Routes>
          {inspectorRoutes || logisticsRoutes || accountableRoutes || (
            <>
              <Route index element={<RoleDashboard role={role} canViewSummary />} />
              {navigationItems
                .filter((item) => item.path !== dashboardPath)
                .map((item) => (
                  <Route
                    key={item.id}
                    path={item.path.slice(dashboardPath.length + 1)}
                    element={<WorkspacePlaceholder title={item.label} description={item.description} />}
                  />
                ))}
            </>
          )}
          <Route path="*" element={<Navigate to={dashboardPath} replace />} />
        </Routes>
      )}
    </AdminLayout>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [user, setUser] = React.useState<User | null>(null);
  const [privacyConsentRequired, setPrivacyConsentRequired] = React.useState<boolean>(false);
  const [authChecked, setAuthChecked] = React.useState<boolean>(false);
  const [authBootstrapError, setAuthBootstrapError] = React.useState('');
  const [, setApplicationDateRevision] = React.useState(0);

  React.useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      let authenticatedProfileLoaded = false;
      try {
        // Check if user is authenticated by getting profile
        const response = await authService.getProfile();
        if (!isMounted) return;
        if (response.success && response.user) {
          authenticatedProfileLoaded = true;
          configureApplicationDateContext(response);
          // Load user permissions
          await permissionService.loadPermissions();
          const privacyStatus = await privacyService.getStatus();
          if (!isMounted) return;
          setUser(response.user);
          setIsAuthenticated(true);
          setPrivacyConsentRequired(!privacyStatus.has_active_consent);
          setAuthBootstrapError('');
        } else {
          if (!isMounted) return;
          setIsAuthenticated(false);
          setUser(null);
          setPrivacyConsentRequired(false);
          permissionService.clearPermissions();
          clearApplicationDateContext();
        }
      } catch (error) {
        if (!isMounted) return;
        if (ErrorHandler.isUnauthorizedError(error)) {
          setIsAuthenticated(false);
          setUser(null);
          setPrivacyConsentRequired(false);
          permissionService.clearPermissions();
          clearApplicationDateContext();
          return;
        }
        console.error('Failed to fetch profile:', error);
        if (authenticatedProfileLoaded) {
          setAuthBootstrapError(
            'Authentication succeeded, but your workspace could not be loaded.'
          );
        }
        setIsAuthenticated(false);
        setUser(null);
        setPrivacyConsentRequired(false);
        permissionService.clearPermissions();
        clearApplicationDateContext();
      } finally {
        if (isMounted) {
          setAuthChecked(true);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
      clearApplicationDateContext();
    };
  }, []); // Empty dependency array to prevent infinite loop

  React.useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;
    let rolloverTimer: number | undefined;

    const scheduleRollover = () => {
      const delay = millisecondsUntilApplicationDateRollover();
      if (delay === null) return;

      rolloverTimer = window.setTimeout(() => {
        if (cancelled) return;
        setApplicationDateRevision((revision) => revision + 1);
        scheduleRollover();
      }, delay);
    };

    scheduleRollover();
    return () => {
      cancelled = true;
      if (rolloverTimer !== undefined) window.clearTimeout(rolloverTimer);
    };
  }, [isAuthenticated]);

  const handleUserLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      permissionService.clearPermissions();
      clearApplicationDateContext();
      setIsAuthenticated(false);
      setUser(null);
      setPrivacyConsentRequired(false);
    }
  };

  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        color: '#333',
        fontFamily: 'Arial, sans-serif',
      }}>
        Loading...
      </div>
    );
  }

  const roleHome = getDashboardPath(user);
  const authenticatedHome = privacyConsentRequired ? '/privacy-agreement' : roleHome;
  const allowedRoutes = getAllowedRoutes(user);
  const canAccessRoute = (path: string) => allowedRoutes.includes(path);
  const canAccessOperationalWorkspace = (path: string): boolean =>
    isAdminUser(user) || canAccessRoute(path);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to={authenticatedHome} replace /> : <Login initialError={authBootstrapError} />
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/privacy-agreement"
          element={
            isAuthenticated && user ? (
              <PrivacyAgreement
                authenticatedHome={roleHome}
                onAccepted={() => setPrivacyConsentRequired(false)}
                onLogout={handleUserLogout}
                requireAcceptance={privacyConsentRequired}
              />
            ) : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/dashboard"
          element={<Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />}
        />
        <Route
          path="/admin-dashboard/*"
          element={
            isAuthenticated && !privacyConsentRequired && permissionService.hasPermission('admin.dashboard.read') ? (
              user ? (
                <AdminLayout title="Administrator Dashboard" onLogout={handleUserLogout} userName={user.username}>
                  <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="enter/logistics" element={<AdminDepartmentWorkspacePicker workspace="logistics" />} />
                    <Route path="enter/accountable" element={<AdminDepartmentWorkspacePicker workspace="accountable" />} />
                    <Route path="enter/inspector" element={<Navigate to="/inspector-dashboard" replace />} />
                    {/* These legacy admin umbrella permissions remain the route guards until RBAC codes are split further. */}
                    <Route 
                      path="users" 
                      element={
                        <ProtectedRoute requiredPermissions={['admin.user.manage']}>
                          <UserManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="roles" 
                      element={
                        <ProtectedRoute requiredPermissions={['admin.role.manage']}>
                          <RoleManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="departments"
                      element={
                        <ProtectedRoute requiredPermissions={['admin.user.manage']}>
                          <DepartmentManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="frs-qa" 
                      element={<Navigate to="/admin-dashboard/security-center" replace />} 
                    />
                    <Route
                      path="system-governance"
                      element={
                        <ProtectedRoute requiredPermissions={['admin.dashboard.read']}>
                          <SystemGovernance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="audit-logs"
                      element={
                        <ProtectedRoute requiredPermissions={['admin.dashboard.read']}>
                          <AuditLogs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="data-privacy"
                      element={
                        <ProtectedRoute requiredPermissions={['admin.dashboard.read']}>
                          <DataPrivacyCompliance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="archives"
                      element={
                        <ProtectedRoute requiredPermissions={['records.archive.view']}>
                          <ArchiveManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="security-center"
                      element={
                        <ProtectedRoute requiredPermissions={['admin.dashboard.read']}>
                          <SecurityCenter />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<Navigate to="/admin-dashboard" replace />} />
                  </Routes>
                </AdminLayout>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '100vh',
                  backgroundColor: '#f0f2f5',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div>Loading admin dashboard...</div>
                  </div>
                </div>
              )
            ) : <Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />
          }
        />
        <Route
          path="/profile"
          element={
            isAuthenticated && user ? (
              <AdminLayout
                title="My Profile"
                onLogout={handleUserLogout}
                userName={user.username}
                roleLabel={user.role_display}
                dashboardPath={authenticatedHome}
                sectionLabel="Profile"
              >
                <ProfilePage user={user} onPasswordChanged={handleUserLogout} />
              </AdminLayout>
            ) : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/logistics-dashboard/*"
          element={
            isAuthenticated && !privacyConsentRequired && user && canAccessOperationalWorkspace('/logistics-dashboard') ? (
              <OperationalWorkspace
                user={user}
                workspace="logistics"
                role="CENTRAL_LOGISTICS_OFFICER"
                title="Logistics Dashboard"
                sectionLabel="Logistics Officer"
                onLogout={handleUserLogout}
              />
            ) : <Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />
          }
        />
        <Route
          path="/inspector-dashboard/*"
          element={
            isAuthenticated && !privacyConsentRequired && user && canAccessOperationalWorkspace('/inspector-dashboard') ? (
              <OperationalWorkspace
                user={user}
                workspace="inspector"
                role="INSPECTION_OFFICER"
                title="Inspector Dashboard"
                sectionLabel="Inspector"
                onLogout={handleUserLogout}
              />
            ) : <Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />
          }
        />
        <Route
          path="/accountable-dashboard/*"
          element={
            isAuthenticated && !privacyConsentRequired && user && canAccessOperationalWorkspace('/accountable-dashboard') ? (
              <OperationalWorkspace
                user={user}
                workspace="accountable"
                role="ACCOUNTABLE_PNP_OFFICER"
                title="Accountable Officer Dashboard"
                sectionLabel="Accountable Officer"
                onLogout={handleUserLogout}
              />
            ) : <Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />
          }
        />
        <Route
          path="/admin/*"
          element={
            isAuthenticated && !privacyConsentRequired && permissionService.hasPermission('admin.dashboard.read') ? (
              <Navigate to="/admin-dashboard" replace />
            ) : <Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />
          }
        />
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />}
        />
        <Route path="*" element={<Navigate to={isAuthenticated ? authenticatedHome : '/login'} replace />} />
      </Routes>
    </Router>
  );
};

export default App;
